import axios from 'axios'
import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { BASE_URL, authToken } from '../../../../../../config/constants'
import { Workshop, Special } from '../types/workshopsAndSpecials'

interface WorkshopsAndSpecialsContextProps {
    workshops: Workshop[] | null
    specials: Special[] | null
    fetchWorkshops: () => Promise<void>
    revalidateWorkshops: () => Promise<void>
    isLoadingWorkshopsAndSpecials: boolean
    isRevalidatingWorkshopsAndSpecials: boolean
    errorWorkshops: string | null
}

const WorkshopsAndSpecialsContext = createContext<
    WorkshopsAndSpecialsContextProps | undefined
>(undefined)

export const WorkshopsAndSpecialsProvider: React.FC<{
    children: React.ReactNode
}> = ({ children }) => {
    const [workshops, setWorkshops] = useState<Workshop[] | null>(null)
    const [specials, setSpecials] = useState<Special[] | null>(null)
    const [isLoadingWorkshopsAndSpecials, setIsLoadingWorkshopsAndSpecials] =
        useState(false)
    const [
        isRevalidatingWorkshopsAndSpecials,
        setIsRevalidatingWorkshopsAndSpecials,
    ] = useState(false)
    const [errorWorkshops, setErrorWorkshops] = useState<string | null>(null)
    const workshopsRequestRef = useRef<Promise<void> | null>(null)

    const loadWorkshops = async (background = false) => {
        if (workshopsRequestRef.current) {
            return workshopsRequestRef.current
        }

        if (background) {
            setIsRevalidatingWorkshopsAndSpecials(true)
        } else {
            setIsLoadingWorkshopsAndSpecials(true)
        }
        setErrorWorkshops(null)

        const request = (async () => {
            const [workshopResponse, specialResponse] = await Promise.all([
                axios.get(`${BASE_URL}/api/workshops?populate=*`, {
                    headers: { Authorization: `Bearer ${authToken}` },
                }),
                axios.get(`${BASE_URL}/api/specials?populate=*`, {
                    headers: { Authorization: `Bearer ${authToken}` },
                }),
            ])

            setWorkshops(workshopResponse.data.data)
            setSpecials(specialResponse.data.data)
        })()

        workshopsRequestRef.current = request

        try {
            await request
        } catch (error: unknown) {
            console.error(
                background
                    ? 'Error revalidating workshops and specials:'
                    : 'Error fetching workshops and specials:',
                error
            )
            if (!background) {
                setErrorWorkshops('Failed to fetch workshops and specials.')
            }
        } finally {
            workshopsRequestRef.current = null
            if (background) {
                setIsRevalidatingWorkshopsAndSpecials(false)
            } else {
                setIsLoadingWorkshopsAndSpecials(false)
            }
        }
    }

    const fetchWorkshops = async () => {
        await loadWorkshops(false)
    }

    const revalidateWorkshops = async () => {
        await loadWorkshops(workshops !== null || specials !== null)
    }

    useEffect(() => {
        fetchWorkshops()
    }, [])

    return (
        <WorkshopsAndSpecialsContext.Provider
            value={{
                workshops,
                specials,
                fetchWorkshops,
                revalidateWorkshops,
                isLoadingWorkshopsAndSpecials,
                isRevalidatingWorkshopsAndSpecials,
                errorWorkshops,
            }}
        >
            {children}
        </WorkshopsAndSpecialsContext.Provider>
    )
}

export const useWorkshopsAndSpecials = () => {
    const context = useContext(WorkshopsAndSpecialsContext)
    if (!context) {
        throw new Error(
            'useWorkshopsAndSpecials must be used within a WorkshopsAndSpecialsProvider'
        )
    }
    return context
}
